import React, { useCallback, useEffect, useState } from 'react';
import { Box, Button } from '@mui/material';
import { useParams } from 'react-router-dom';
import PageTitle from 'pages/components/PageTitle';
import { LoadPanel } from 'devextreme-react';
import { HistoricalResultsApis } from 'hooks/apis/HistoricalResultsApis';
import { IAccessRequest } from 'interfaces/AccessRequest/IAccessRequest';
import { PricelistApis } from 'hooks/apis/PricelistApis';
import { IAccessRequestInfo } from 'interfaces/AccessRequest/IAccessRequestInfo';

const ConfirmAccess = () => {
  const { type, id, token, accessType } = useParams();
  const { getAccessDetails, postSetAccess } = HistoricalResultsApis();
  const { getPricelistAccessDetails, postSetPricelistAccess } = PricelistApis();
  const [accessDetails, setAccessDetails] = useState<IAccessRequestInfo>(null);
  const [loadingData, setLoadingData] = useState(true);
  const [sendingRequest, setSendingRequest] = useState(false);
  const [requestSent, setRequestSent] = useState(false);
  const [error, setError] = useState<string>(null);

  const sendHistoricalRequest = useCallback(
    (data: IAccessRequest) => {
      setSendingRequest(true);
      postSetAccess(data)
        .then((response) => {
          if (response && response.errorMessage) {
            if (response.errorMessage === 'Unavailable') {
              setError('No request available, this may have been already processed');
            } else {
              setError(response?.errorMessage);
            }
          }
        })
        .finally(() => {
          setRequestSent(true);
          setSendingRequest(false);
        });
    },
    [postSetAccess]
  );

  const sendPricelistRequest = useCallback(
    (data: IAccessRequest) => {
      setSendingRequest(true);
      postSetPricelistAccess(data)
        .then((response) => {
          if (response && response.errorMessage) {
            if (response.errorMessage === 'Unavailable') {
              setError('No request available, this may have been already processed');
            } else {
              setError(response?.errorMessage);
            }
          }
        })
        .finally(() => {
          setRequestSent(true);
          setSendingRequest(false);
        });
    },
    [postSetPricelistAccess]
  );

  const approveRequest = useCallback(() => {
    if (!type || !id || !token || !accessType) {
      return;
    }

    const data: IAccessRequest = {
      Id: parseInt(id),
      Token: token,
      AccessType: parseInt(accessType),
    };

    switch (type) {
      case 'historical':
        sendHistoricalRequest(data);
        break;
      case 'pricelist':
        sendPricelistRequest(data);
        break;
    }
  }, [id, token, type, accessType, sendHistoricalRequest, sendPricelistRequest]);

  const getHistoricalDetails = useCallback(
    (data: IAccessRequest) => {
      getAccessDetails(data).then((response) => {
        if (response && !response.errorMessage) {
          setAccessDetails(response);
        } else {
          setError('No request available, this may have been already processed');
        }
        setLoadingData(false);
      });
    },
    [getAccessDetails]
  );

  const getPricelistDetails = useCallback(
    (data: IAccessRequest) => {
      getPricelistAccessDetails(data).then((response) => {
        if (response && !response.errorMessage) {
          setAccessDetails(response);
        } else {
          setError('No request available, this may have been already processed');
        }
        setLoadingData(false);
      });
    },
    [getPricelistAccessDetails]
  );

  useEffect(() => {
    if (!type || !id || !token || !accessType) {
      setError('Data unavailable');
      setLoadingData(false);
    }

    const data: IAccessRequest = {
      Id: parseInt(id),
      Token: token,
      AccessType: parseInt(accessType),
    };

    switch (type) {
      case 'historical':
        getHistoricalDetails(data);
        break;
      case 'pricelist':
        getPricelistDetails(data);
        break;
    }
  }, [id, token, type, accessType, getHistoricalDetails, getPricelistDetails]);

  return (
    <>
      <PageTitle pageTitle={`Confirm user access for ${type}`} />

      <LoadPanel height={100} width={250} message="Loading request information" visible={loadingData} />
      <LoadPanel height={100} width={250} message="Sending access request" visible={sendingRequest} />

      {!loadingData && !requestSent && !error && !sendingRequest ? (
        <Box>
          Are you sure you want to {accessDetails?.AccessMessage === 'Deny' ? 'Deny' : `grant ${accessDetails?.AccessMessage}`} access to{' '}
          {accessDetails?.Email} for {accessDetails?.Client} <Button className="siteBtn" onClick={() => approveRequest()}>Submit</Button>
        </Box>
      ) : null}
      {requestSent && !error ? (
        <Box>
          Success - {accessDetails?.Email} access rights updated for {accessDetails?.Client} | {accessDetails?.AccessMessage}{' '}
        </Box>
      ) : null}

      {error ? <Box><h2>Error</h2>{error}</Box> : null}
    </>
  );
};

export default ConfirmAccess;
